import get from 'lodash.get';
import type { ResponsiveBreakpointNames } from '~/services/layout/layout';

/**
 * Maps responsive breakpoint names to corresponding Tailwind CSS class name prefixes.
 * @param breakpoint - responsive breakpoint name
 * @returns Tailwind CSS class name prefix
 */
export const mapBreakpointToClassNames = (
  breakpoint: ResponsiveBreakpointNames
) => {
  // transform values from ResponsiveBreakpointNames to match tailwind class names
  // mobile:max-tablet:, tablet:max-desktop:, desktop:max-large-desktop, large-desktop:
  switch (breakpoint) {
    case 'tablet':
      return 'tablet:max-desktop:';
    case 'desktop':
      return 'desktop:max-large-desktop:';
    case 'large-desktop':
      return 'large-desktop:';
    case 'mobile':
      return 'mobile:max-tablet:';
    default:
      return '';
  }
};

/**
 * Maps responsive breakpoint styles to CSS class names using a transform function
 * @param styles - Object with responsive breakpoint keys and their style values
 * @param transformFn - Function that transforms each style value to a CSS class name
 * @returns Array of responsive CSS class names
 */
export const mapResponsiveStylesToClassNames = <T>(
  styles: Record<ResponsiveBreakpointNames, T>,
  transformFn: (value: T, key: ResponsiveBreakpointNames) => string
): string[] => {
  return Object.entries(styles).reduce((acc, [key, val]) => {
    if (key === 'unset') return acc; // skip unset breakpoint
    const res = transformFn(val as T, key as ResponsiveBreakpointNames);

    if (!res) return acc;

    acc.push(
      Array.isArray(res)
        ? res
            .filter((item) => !!item)
            .map(
              (r) =>
                mapBreakpointToClassNames(key as ResponsiveBreakpointNames) + r
            )
            .join(' ')
        : mapBreakpointToClassNames(key as ResponsiveBreakpointNames) + res
    );
    return acc;
  }, [] as string[]);
};

/**
 * Maps responsive styles to an array using a transform function
 * @param styles - Object with responsive breakpoint keys and their style values
 * @param transformFn - Function that transforms each style value to an array element
 * @returns Array of transformed values
 */
export const mapResponsiveStylesToArray = <T, V>(
  styles: Record<ResponsiveBreakpointNames, T>,
  transformFn: (value: T, key: ResponsiveBreakpointNames) => V
): V[] => {
  return Object.entries(styles).reduce((acc, [key, val]) => {
    if (key === 'unset') return acc; // skip unset breakpoint
    const res = transformFn(val as T, key as ResponsiveBreakpointNames);

    if (!res) return acc;

    acc.push(res);

    return acc;
  }, [] as V[]);
};

export const getTitleKeyToUse = (styles: any) =>
  get(
    styles,
    get(styles, 'title_data_key') === 'other'
      ? 'title_custom_data_key'
      : 'title_data_key'
  );
